import {
  EcommerceAPIApi as EcommerceApi,
  MonthlyAccountSaveRequest,
  CreateNewMonthlyAccountSourceEnum,
  CreateNewMonthlyPermitForMonthlyAccountSourceEnum,
  ReservationMonthlyAccountModificationNewTaskTypeEnum,
  VehiclePlateDuplicateCheckSourceEnum,
  type MonthlyCar,
  type MonthlyQuote,
  type MonthlyQuoteSaveRequest,
  type PermitSaveRequest,
  type VehicleValidationResponse,
} from '@parkingbase/ecommerce-api-dev';

import type { RawAxiosRequestConfig } from 'axios';
import { PAYMENT_TIMEOUT_MS } from '@/lib/api/payment-timeout';
import { BaseApiService } from './baseApi.service';

export type MonthlyTaskSaveRequest = Record<
  string,
  string | boolean | Date | object | Array<unknown>
>;

export class MonthlyAccountService extends BaseApiService {
  protected monthlyAccountApi: EcommerceApi;

  constructor() {
    super();
    this.monthlyAccountApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  async createMonthlyTaskNew(
    companyId: string,
    saveRequest: MonthlyTaskSaveRequest,
    taskType: ReservationMonthlyAccountModificationNewTaskTypeEnum,
    monthlyAccountId: string,
    options?: RawAxiosRequestConfig
  ) {
    // Forward options only when given so existing callers make the same call.
    const response = await this.monthlyAccountApi.reservationMonthlyAccountModificationNew(
      companyId,
      saveRequest,
      taskType,
      monthlyAccountId,
      ...(options ? [options] : [])
    );
    return response.data;
  }

  async sendAccessCredential(
    companyId: string,
    accountId: string,
    permitId: string,
    credentialId: string,
    type: 'email' | 'sms'
  ) {
    await this.monthlyAccountApi.sendAccessCredentialsCommunicationFromReservation(
      companyId,
      accountId,
      permitId,
      credentialId,
      type
    );
  }

  async createNewMonthlyAccount(
    company: string,
    saveRequest: MonthlyAccountSaveRequest,
    source?: CreateNewMonthlyAccountSourceEnum,
    options?: RawAxiosRequestConfig
  ) {
    // PB-4667: invoice-per-cycle generation + the gateway charge + the approval email all
    // run synchronously server-side, so a multi-cycle purchase can outlast the default 30s.
    // Aborting mid-charge is worse than waiting — the card is charged, yet Retry is offered.
    // PB-7524: the 180s payment timeout always wins; the spread keeps the reCAPTCHA header.
    const response = await this.monthlyAccountApi.createNewMonthlyAccount(
      company,
      saveRequest,
      source,
      { ...options, timeout: PAYMENT_TIMEOUT_MS }
    );
    return response.data;
  }

  async getDisclaimerContract(
    company: string,
    garageId: string,
    disclaimerType: string,
    accountId?: string
  ) {
    const response = await this.monthlyAccountApi.getDisclaimerContract(
      company,
      garageId,
      disclaimerType,
      accountId
    );
    return response.data;
  }

  async vehiclePlateDuplicateCheck(
    garageId: string,
    monthlyCar: MonthlyCar,
    source?: VehiclePlateDuplicateCheckSourceEnum,
    permitRateId?: string,
    accountId?: string,
    options?: RawAxiosRequestConfig
  ): Promise<VehicleValidationResponse> {
    const response = await this.monthlyAccountApi.vehiclePlateDuplicateCheck(
      garageId,
      monthlyCar,
      source,
      permitRateId,
      accountId,
      options
    );
    return response.data;
  }

  async vehiclePlateCheck(
    garageId: string,
    monthlyCar: MonthlyCar,
    permitRateId?: string,
    options?: RawAxiosRequestConfig
  ): Promise<VehicleValidationResponse> {
    return this.vehiclePlateDuplicateCheck(
      garageId,
      monthlyCar,
      VehiclePlateDuplicateCheckSourceEnum.RESERVATION,
      permitRateId,
      undefined,
      options
    );
  }

  async getDisclaimerContractForPermitRate(
    company: string,
    garageId: string,
    disclaimerType: string,
    templateId: string
  ) {
    const response = await this.monthlyAccountApi.getDisclaimerContractForPermitRate(
      company,
      garageId,
      disclaimerType,
      templateId
    );
    return response.data;
  }

  async createNewMonthlyPermitForMonthlyAccount(
    company: string,
    accountId: string,
    permitSaveRequest: PermitSaveRequest,
    source?: CreateNewMonthlyPermitForMonthlyAccountSourceEnum,
    options?: RawAxiosRequestConfig
  ) {
    const response = await this.monthlyAccountApi.createNewMonthlyPermitForMonthlyAccount(
      company,
      accountId,
      permitSaveRequest,
      source,
      undefined,
      { ...options, timeout: PAYMENT_TIMEOUT_MS }
    );
    return response.data;
  }

  async getMonthlyAccountForReservation(id: string): Promise<MonthlyQuote> {
    const response = await this.monthlyAccountApi.getMonthlyAccountForReservation(id);
    return response.data;
  }

  async monthlyQuoteSignUp(
    company: string,
    saveRequest: MonthlyQuoteSaveRequest,
    options?: RawAxiosRequestConfig
  ) {
    const response = await this.monthlyAccountApi.monthlyQuoteSignUp(company, saveRequest, {
      ...options,
      timeout: PAYMENT_TIMEOUT_MS,
    });
    return response.data;
  }

  async updateMonthlyNotification(
    company: string,
    monthlyAccountId: string,
    notificationMethods: string[]
  ) {
    const response = await this.monthlyAccountApi.updateMonthlyNotification(
      company,
      monthlyAccountId,
      notificationMethods
    );
    return response.data;
  }
}
